import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/pages/admin/AdminDashboard";
import { toast } from "sonner";
import { Check, X, Trash2 } from "lucide-react";

const fmtMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export default function AdminLeaves() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");

  const [month, setMonth] = useState(fmtMonth(new Date()));
  const [summary, setSummary] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = (status) => api.get(`/leaves?status=${status}`).then((r) => setItems(r.data));
  const loadSummary = (m) => api.get(`/leaves/summary?month=${m}`).then((r) => setSummary(r.data));

  useEffect(() => { if (tab !== "summary") load(tab); }, [tab]);
  useEffect(() => { if (tab === "summary") loadSummary(month); }, [tab, month]);

  const approve = async (lv) => {
    try {
      await api.post(`/leaves/${lv.id}/approval`, { action: "approve" });
      toast.success("Leave approved");
      load(tab);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const submitReject = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post(`/leaves/${rejectFor.id}/approval`, { action: "reject", rejection_reason: reason });
      toast.success("Leave rejected");
      setRejectFor(null); setReason("");
      load(tab);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/leaves/${deleteTarget.id}`);
      toast.success("Leave deleted");
      setDeleteTarget(null);
      load(tab);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <Shell title="Leave Requests" subtitle="Approve or reject leave requests; view monthly summary by salesperson.">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending" data-testid="leave-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="leave-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="leave-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="summary" data-testid="leave-tab-summary">Monthly Summary</TabsTrigger>
        </TabsList>

        {tab !== "summary" && (
          <TabsContent value={tab}>
            <Card className="border-zinc-200 shadow-none rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="font-bold text-zinc-950">Salesperson</TableHead>
                    <TableHead className="font-bold text-zinc-950">From</TableHead>
                    <TableHead className="font-bold text-zinc-950">To</TableHead>
                    <TableHead className="font-bold text-zinc-950 text-right">Days</TableHead>
                    <TableHead className="font-bold text-zinc-950">Reason</TableHead>
                    <TableHead className="font-bold text-zinc-950 text-right">Status / Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-zinc-500">No leave requests.</TableCell></TableRow>
                  )}
                  {items.map((lv) => (
                    <TableRow key={lv.id} data-testid={`leave-row-${lv.id}`}>
                      <TableCell className="font-semibold">{lv.salesperson_name}</TableCell>
                      <TableCell>{lv.from_date}</TableCell>
                      <TableCell>{lv.to_date}</TableCell>
                      <TableCell className="text-right tabular-nums">{lv.total_days}</TableCell>
                      <TableCell className="max-w-xs truncate" title={lv.reason}>{lv.reason}</TableCell>
                      <TableCell className="text-right">
                        {tab === "pending" ? (
                          <div className="flex gap-2 justify-end items-center">
                            <Button size="sm" onClick={() => approve(lv)} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid={`leave-approve-${lv.id}`}>
                              <Check size={14} className="mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setRejectFor(lv); setReason(""); }} data-testid={`leave-reject-${lv.id}`}>
                              <X size={14} className="mr-1" /> Reject
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(lv)} data-testid={`leave-delete-${lv.id}`}>
                              <Trash2 size={14} className="text-rose-600" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <div className="space-y-1">
                              <StatusBadge status={lv.status} />
                              {lv.rejection_reason && <div className="text-xs text-rose-700 italic">{lv.rejection_reason}</div>}
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(lv)} data-testid={`leave-delete-${lv.id}`}>
                              <Trash2 size={14} className="text-rose-600" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="summary">
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-semibold">Month</label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="summary-month-input" />
          </div>
          <Card className="border-zinc-200 shadow-none rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="font-bold text-zinc-950">Salesperson</TableHead>
                  <TableHead className="font-bold text-zinc-950">Email</TableHead>
                  <TableHead className="font-bold text-zinc-950 text-right">Leave requests</TableHead>
                  <TableHead className="font-bold text-zinc-950 text-right">Total days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!summary || summary.rows.length === 0) && (
                  <TableRow><TableCell colSpan={4} className="text-center py-10 text-zinc-500">No salespeople or no data for this month.</TableCell></TableRow>
                )}
                {summary?.rows?.map((r) => (
                  <TableRow key={r.salesperson_id} data-testid={`summary-row-${r.salesperson_id}`}>
                    <TableCell className="font-semibold">{r.salesperson_name}</TableCell>
                    <TableCell className="text-zinc-600">{r.salesperson_email}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.leave_count}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{r.total_days}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="leave-reject-dialog">
          <DialogHeader><DialogTitle>Reject leave for {rejectFor?.salesperson_name}</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason (required)…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="leave-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button onClick={submitReject} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="leave-reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this leave record?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete the leave entry for{" "}
              <span className="font-semibold">{deleteTarget?.salesperson_name}</span> ({deleteTarget?.from_date} → {deleteTarget?.to_date}).
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-leave-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-leave-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
